import React from 'react';
import getCalculationInputsFromURL from './utils/getCalculationInputsFromURL';
import keyFactsCalculations from "./calculations/keyFactsCalculations";
import currencyFormatter from '../../utils/currencyFormatter';
import formatPercentage from '../../utils/formatPercentage';
import capitalizeWord from '../../utils/capitalizeWord';
import formatCurrencyWithFractionDigits from '../../utils/formatCurrencyWithFractionDigits';
import monthsToYearsFormatter from '../../utils/monthsToYearsFormatter';
import formatNumberToYears from '../../utils/formatNumberToYears';
import { keyProducts } from './KeyFactsCalculator';

const inputs = ['loanAmount', 'objective', 'lvr', 'termOfLoan', 'interestType', 'matchingLoans', 'fixedRatePeriod', 'selectedProduct']
const today = new Date().toLocaleDateString('en-AU')

class KeyFactsDocument extends React.Component {
  state = {
    selectedProduct: 'lowRateHome',
    outputs: null,
    inputValues: null
  };

  async componentDidMount() {
    // Get the input values and selected product from URL
    const inputValues = getCalculationInputsFromURL(document.location, inputs);
    const selectedProduct = (new URL(document.location)).searchParams.get('selectedProduct');
    this.setState({ inputValues, selectedProduct });
    try {
      const outputs = await keyFactsCalculations(inputValues, keyProducts[selectedProduct]);
      this.setState({ outputs });  // Store the outputs in the state
      window.print();
    } catch (error) {
      console.error('Error in keyFactsCalculations:', error);
    }
  }

  render() {
    const { selectedProduct, inputValues, outputs } = this.state;
    if (!inputValues || !outputs) {
      return <p>Loading...</p>;
    }

    return (
      <div className="key-facts-document">
        <h1 className="key-facts-document__section-title">
          Key facts about this home loan
        </h1>
        <table className="key-facts-document__produced-details">
          <tbody>
            <tr>
              <td>
                <p>Date produced: {today}</p>
              </td>
              <td>
                <div className="key-facts-document__logo" />
                <p>Australian credit licence number: 509481</p>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="key-facts-document__disclaimer">
          <span className="key-facts-document__not-an-offer">
            This is not an offer of credit.
          </span>
          &nbsp; This Key Facts Sheet is provided to help you compare this home
          loan with the home loans of other lenders.
        </p>
        <table>
          <thead>
            <tr>
              <th colSpan="2">What you have told us</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Loan Amount:</td>
              <td>{currencyFormatter.format(inputValues.loanAmount)}</td>
            </tr>
            <tr>
              <td>Term of the home loan:</td>
              <td>{inputValues.termOfLoan} years</td>
            </tr>
            <tr>
              <td>Interest type:</td>
              <td>
                {capitalizeWord(inputValues.interestType)}{" "}
                {inputValues.interestType === "fixed"
                  ? `for ${formatNumberToYears(outputs.fixedTerm)}`
                  : null}
              </td>
            </tr>
            <tr>
              <td>Lender and product name:</td>
              <td>{keyProducts[selectedProduct].label}</td>
            </tr>
          </tbody>
        </table>
        <h2 className="key-facts-document__special-title">
          How does this home loan compare?
        </h2>
        <table className="inside-borders blue-background">
          <thead>
            <tr>
              <th colSpan="2">Description of this home loan</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Repayment method</td>
              <td>Principal and interest</td>
            </tr>
            <tr>
              <td>Repayment frequency</td>
              <td>Monthly (other repayment options are available)</td>
            </tr>
            <tr>
              <td>Interest rate</td>
              <td>{formatPercentage(outputs.interestRate, 2)} per annum</td>
            </tr>
            <tr>
              <td>
                <p>Personalised comparison rate</p>
                <p>(interest rate including fees)</p>
              </td>
              <td>{outputs.personalisedComparisonRate}% per annum</td>
            </tr>
          </tbody>
        </table>
        <table className="inside-borders">
          <thead>
            <tr>
              <th colSpan="2">Estimated cost of this home loan</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Total amount to be paid back (including the loan amount and
                fees)
              </td>
              <td className="strong">
                {formatCurrencyWithFractionDigits(
                  outputs.totalAmountToBePaidBack,
                  2
                )}
              </td>
            </tr>
            <tr className="blue-background">
              <td>This means you will pay back</td>
              <td className="strong">
                {formatCurrencyWithFractionDigits(
                  outputs.thisMeansYouWillPayBack,
                  2
                )}{" "}
                for every $1 borrowed
              </td>
            </tr>
            <tr>
              <td>Establishment fees</td>
              <td className="strong">
                {formatCurrencyWithFractionDigits(outputs.establishmentFees, 2)}
              </td>
            </tr>
            <tr>
              <td>Ongoing fees</td>
              <td className="strong">
                <p>
                  {formatCurrencyWithFractionDigits(
                    outputs.ongoingFeesPerMonth,
                    2
                  )}{" "}
                  per month
                </p>
                <p>
                  {formatCurrencyWithFractionDigits(outputs.ongoingFees, 2)} per
                  year
                </p>
              </td>
            </tr>
            <tr>
              <td>Repayment per month (including ongoing fees)</td>
              <td className="strong">
                {formatCurrencyWithFractionDigits(outputs.repaymentPerMonth, 2)}
              </td>
            </tr>
            <tr>
              <td>Repayment per year (including ongoing fees)</td>
              <td className="strong">
                {formatCurrencyWithFractionDigits(outputs.repaymentPerYear, 2)}
              </td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr>
              <td>
                <p>
                  There may be circumstances in which other fees are payable.
                  Fees applicable for the loan you apply for will be shown in
                  the loan contract. You can also obtain a list of fees
                  applicable to this type of loan through our website at{" "}
                  <a
                    href="https://www.yard.com.au/"
                    className="key-facts-document__link"
                  >
                    www.yard.com.au
                  </a>
                  .
                </p>
                <p>
                  Other loan set-up fees, such as valuation fees and lender’s
                  mortgage insurance, and Government charges, such as
                  registration fees and stamp duty on property transfer, have
                  not been included. These will be determined after application.
                </p>
                <p>
                  Additional fees may be payable if you choose to repay your
                  fixed rate home loan early.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <table>
          <thead>
            <tr>
              <th>What happens if interest rates increase?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  This is a variable rate loan. If your interest rate was to
                  increase by 1% per annum, your monthly repayment would
                  increase by around{" "}
                  <span className="strong">
                    {formatCurrencyWithFractionDigits(outputs.whatHappensIf)}
                  </span>
                  .
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <table>
          <thead>
            <tr>
              <th>How can I repay my loan faster?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>
                  This loan allows you to make additional repayments to pay off
                  your home loan faster. If you increased your monthly
                  repayments by $200 a month to{" "}
                  <span className="strong">
                    {formatCurrencyWithFractionDigits(
                      outputs.increasedRepaymentBy
                    )}{" "}
                    you would repay the loan in{" "}
                    {monthsToYearsFormatter(
                      Math.round(outputs.howCanIRepayMyLoanFaster)
                    )}
                    , instead of {inputValues.termOfLoan} years, based on the
                    current variable interest rate stated in this Key Facts
                    Sheet.
                  </span>
                </p>
                <p>
                  Altering the frequency of repayments may also help repay the
                  loan faster.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default KeyFactsDocument;
